import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsSeguridad';
import PermisosModal from '../../Catalogos/Permisos/PermisosModal';
import RecursosModal from '../../Catalogos/Recursos/RecursosModal';
import { getSessionEmpresaId, isSessionSuperAdmin } from '../../../../../utils/SessionUser';

const EMPTY = {
  selectedRoles: [], // Ahora es un array de IDs de roles
  idPermiso: 1,
  idRecurso: '', // opcional, '' = sin recurso
};

const endpointRoles = ENDPOINTS.roles;
const endpointPermisos = ENDPOINTS.permisos;
const endpointRecursos = ENDPOINTS.recursos;

export default function Modal({
  open,
  mode = 'new',
  data = null,
  onClose,
  onSubmit,
  inline = false,
  readOnly = false,
}) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({
    selectedRoles: '',
    idPermiso: '',
    idRecurso: '',
  });

  const validateField = (field, rawValue) => {
    const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    let error = '';

    switch (field) {
      case 'selectedRoles':
        if (!Array.isArray(value) || value.length === 0) error = 'Selecciona al menos un rol.';
        break;
      case 'idPermiso':
        if (!value || value <= 0) error = 'Selecciona un permiso.';
        break;
      case 'idRecurso':
        if (!value || value <= 0) error = 'Selecciona un recurso.';
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  const validateAll = () => {
    const fields = ['selectedRoles', 'idPermiso', 'idRecurso'];
    let isValid = true;
    fields.forEach(f => {
      if (!validateField(f, form[f])) isValid = false;
    });
    return isValid;
  };

  const [rolesOptions, setRolesOptions] = useState([]);
  const [permisosOptions, setPermisosOptions] = useState([]);
  const [recursosOptions, setRecursosOptions] = useState([]);

  const [permisosOpen, setPermisosOpen] = useState(false);
  const [recursosOpen, setRecursosOpen] = useState(false);

  const [savingPermiso, setSavingPermiso] = useState(false);
  const [savingRecurso, setSavingRecurso] = useState(false);

  // Drag and drop state
  const [dragOverColumn, setDragOverColumn] = useState(null);

  // Estado para almacenar todas las asignaciones existentes
  const [allAssignments, setAllAssignments] = useState([]);
  const [preAssignedRoles, setPreAssignedRoles] = useState([]);

  const userEmpresaId = getSessionEmpresaId();
  const isSuperAdmin = isSessionSuperAdmin();

  // cargar catálogos y asignaciones existentes
  const loadCatalogs = async () => {
    try {
      const [resRoles, resPermisos, resRecursos, resAssignments] = await Promise.all([
        axios.get(endpointRoles),
        axios.get(endpointPermisos),
        axios.get(endpointRecursos),
        axios.get(ENDPOINTS.rolesRecursosPermisos),
      ]);

      const listaRoles = (resRoles.data || []).map((x) => Models.roles(x));
      const listaPermisos = (resPermisos.data || []).map((x) => Models.catPermisos(x));
      const listaRecursos = (resRecursos.data || []).map((x) => Models.catRecursos(x));
      const listaAssignments = (resAssignments.data || []).map((x) => Models.rolesRecursosPermisos(x));

      setRolesOptions(listaRoles);
      setPermisosOptions(listaPermisos);
      setRecursosOptions(listaRecursos);
      setAllAssignments(listaAssignments);
      return listaAssignments;
    } catch (e) {
      console.error('Error cargando catálogos de roles/permisos/recursos', e);
      return [];
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    validateField(field, value);

    // Si cambia el permiso o recurso, recargar roles asignados para visualización
    if (field === 'idPermiso' || field === 'idRecurso') {
      // Calcular los valores actualizados
      const updatedIdPermiso = field === 'idPermiso' ? value : form.idPermiso;
      const updatedIdRecurso = field === 'idRecurso' ? value : form.idRecurso;

      // Cargar roles basándose en la nueva selección
      loadRolesForCurrentSelection(updatedIdPermiso, updatedIdRecurso);
    }
  };

  // Función para cargar roles basándose en la selección actual de permiso y recurso
  const loadRolesForCurrentSelection = (idPermiso, idRecurso) => {
    if (!idPermiso || !idRecurso || allAssignments.length === 0) {
      setForm(prev => ({ ...prev, selectedRoles: [] }));
      return;
    }

    const existingRoles = allAssignments
      .filter(a =>
        a.idPermiso === Number(idPermiso) &&
        a.idRecurso === Number(idRecurso)
      )
      .map(a => a.idRol);

    setForm(prev => ({ ...prev, selectedRoles: existingRoles }));
  };

  useEffect(() => {
    if (!open) return;
    loadCatalogs();
  }, [open]);

  // setear datos en edición o limpiar en modo new
  useEffect(() => {
    if (mode === 'edit' && data) {
      const filled = {
        selectedRoles: Array.isArray(data.selectedRoles) ? data.selectedRoles : (data.idRol ? [data.idRol] : []),
        idPermiso: data.idPermiso ?? 0,
        idRecurso: data.idRecurso ?? '',
      };
      setForm(filled);
      validateField('selectedRoles', filled.selectedRoles);
      validateField('idPermiso', filled.idPermiso);
      validateField('idRecurso', filled.idRecurso);
    } else {
      setForm(EMPTY);
      setErrors({ selectedRoles: '', idPermiso: '', idRecurso: '' });
    }
  }, [mode, data]);

  // Calculate available and selected roles
  const availableRoles = useMemo(() => {
    const selectedIds = new Set(form.selectedRoles);
    return rolesOptions.filter(r => {
      if (selectedIds.has(r.idRol)) return false;
      // Si no es super admin, no puede ver/asignar roles globales
      if (!isSuperAdmin && !r.idEmpresa) return false;
      // Solo puede ver roles de su propia empresa
      if (!isSuperAdmin && r.idEmpresa !== userEmpresaId) return false;
      return true;
    });
  }, [rolesOptions, form.selectedRoles, isSuperAdmin, userEmpresaId]);

  const selectedRolesData = useMemo(() => {
    return form.selectedRoles.map(idRol => {
      const role = rolesOptions.find(r => r.idRol === idRol);
      return role || { idRol, nombre: `Rol #${idRol}` };
    });
  }, [form.selectedRoles, rolesOptions]);

  // Recursos usados en otras asignaciones
  const usedRecursos = useMemo(() => {
    const set = new Set();
    allAssignments.forEach((a) => {
      if (a.idRecurso) {
        set.add(Number(a.idRecurso));
      }
    });
    return set;
  }, [allAssignments]);

  // Filtrar y ordenar recursos: todos los tipos, no usados primero (con ★), luego usados
  const filteredRecursos = useMemo(() => {
    const unused = [];
    const used = [];

    recursosOptions.forEach((r) => {
      if (usedRecursos.has(r.idRecurso)) {
        used.push(r);
      } else {
        unused.push(r);
      }
    });

    const unusedWithStar = unused.map((r) => ({
      ...r,
      displayName: `★ ${r.nombre}`,
    }));

    const usedWithName = used.map((r) => ({
      ...r,
      displayName: r.nombre,
    }));

    return [...unusedWithStar, ...usedWithName];
  }, [recursosOptions, usedRecursos]);

  if (!open) return null;

  // --- Drag and Drop Handlers ---
  const handleDragStart = (e, item, source) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ item, source }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, targetColumn) => {
    e.preventDefault();
    if (dragOverColumn !== targetColumn) {
      setDragOverColumn(targetColumn);
    }
  };

  const handleDrop = (e, targetColumn) => {
    e.preventDefault();
    setDragOverColumn(null);

    const dataStr = e.dataTransfer.getData('application/json');
    if (!dataStr) return;

    const { item, source } = JSON.parse(dataStr);

    if (source === targetColumn) return;

    if (targetColumn === 'selected' && source === 'available') {
      addRoleLocal(item);
    } else if (targetColumn === 'available' && source === 'selected') {
      removeRoleLocal(item);
    }
  };

  const addRoleLocal = (roleItem) => {
    // Protección adicional por si hacen drag & drop de un rol global
    if (!isSuperAdmin && !roleItem.idEmpresa) return;

    if (form.selectedRoles.includes(roleItem.idRol)) return;
    const newSelected = [...form.selectedRoles, roleItem.idRol];
    handleChange('selectedRoles', newSelected);
  };

  const removeRoleLocal = (roleItem) => {
    // Protección adicional: no puede remover roles globales si no es superadmin
    if (!isSuperAdmin && !roleItem.idEmpresa) return;

    const newSelected = form.selectedRoles.filter(id => id !== roleItem.idRol);
    handleChange('selectedRoles', newSelected);
  };

  const hasErrors = Object.values(errors).some((e) => !!e);
  const isSaveDisabled = form.selectedRoles.length === 0 || !form.idPermiso || !form.idRecurso || hasErrors;

  const errorStyle = {
    color: '#b91c1c',
    fontSize: '.8rem',
    display: 'block',
    marginTop: '4px',
  };

  // --- handlers para crear nuevos registros desde los modales hijos ---

  const handleSubmitNuevoPermiso = async (permisoForm) => {
    try {
      setSavingPermiso(true);

      const payload = {
        nombre: permisoForm.nombre ?? '',
        descripcion: permisoForm.descripcion ?? '',
        activo: permisoForm.activo ?? true,
      };

      const res = await axios.post(endpointPermisos, payload);
      const created = res?.data || {};

      // Refrescar catálogo
      await loadCatalogs();

      // Extraer el ID de forma robusta
      let newId = 0;
      if (typeof created === 'number') {
        newId = created;
      } else if (typeof created === 'object' && created !== null) {
        newId = created.idPermiso ?? created.IdPermiso ?? created.id ?? created.Id ?? 0;
      }

      if (newId && newId > 0) {
        // Usar setTimeout para asegurar que el select se haya actualizado con las nuevas opciones
        setTimeout(() => {
          setForm((f) => ({ ...f, idPermiso: newId }));
        }, 100);
      }

      setPermisosOpen(false);
    } catch (e) {
      console.error(e);
      alert('No se pudo crear el permiso.');
    } finally {
      setSavingPermiso(false);
    }
  };

  const handleSubmitNuevoRecurso = async (recursoForm) => {
    try {
      setSavingRecurso(true);

      const res = await axios.post(endpointRecursos, recursoForm);
      const created = res?.data || {};

      // Debug: Ver la respuesta completa
      console.log('[RolesRecursosPermisosModal] Respuesta completa del API:', res);
      console.log('[RolesRecursosPermisosModal] Datos del recurso creado:', created);

      // Extraer el ID del recurso creado - intentar todas las variantes posibles
      let newId = 0;

      // Si la respuesta es directamente un número
      if (typeof created === 'number') {
        newId = created;
      }
      // Si es un objeto, buscar el ID en diferentes propiedades
      else if (typeof created === 'object' && created !== null) {
        newId = created.idRecurso ||
          created.IdRecurso ||
          created.id ||
          created.Id ||
          created.ID ||
          0;
      }

      console.log('[RolesRecursosPermisosModal] ID extraído del recurso:', newId);

      // Refrescar catálogo de recursos y obtener asignaciones frescas
      const freshAssignments = await loadCatalogs();

      // Seleccionar automáticamente el nuevo recurso en el select
      if (newId && newId > 0) {
        // Usar setTimeout para asegurar que el estado se actualice después del render
        setTimeout(() => {
          setForm((f) => {
            console.log('[RolesRecursosPermisosModal] Seleccionando recurso con ID:', newId);

            // Buscar si ya existen asignaciones para este nuevo recurso (ej. automáticas por SP)
            // Usamos f.idPermiso actual y el newId del recurso
            const preAssignedRolesData = (freshAssignments || [])
              .filter(a =>
                a.idPermiso === Number(f.idPermiso) &&
                a.idRecurso === Number(newId)
              )
              .map(a => a.idRol);

            console.log('[RolesRecursosPermisosModal] Roles pre-asignados detectados:', preAssignedRolesData);
            setPreAssignedRoles(preAssignedRolesData);

            return {
              ...f,
              idRecurso: newId,
              selectedRoles: preAssignedRolesData
            };
          });
        }, 100);
      } else {
        console.warn('[RolesRecursosPermisosModal] ⚠️ No se pudo extraer un ID válido del recurso creado');
      }

      setRecursosOpen(false);
    } catch (e) {
      console.error('[RolesRecursosPermisosModal] Error al crear recurso:', e);
      alert('No se pudo crear el recurso.');
    } finally {
      setSavingRecurso(false);
    }
  };

  const Header = (
    <div
      className="roles-topbar"
      style={{
        marginBottom: '0.6rem',
        paddingRight: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
      }}>
      <h3 className="roles-title" style={{ marginRight: 0 }}>
        {mode === 'new' ? 'Nuevo Rol-Recurso-Permiso' : 'Editar Rol-Recurso-Permiso'}
      </h3>
      <button className="roles-btn" onClick={onClose} aria-label="Cerrar">✕</button>
    </div>
  );

  const Body = (
    <div
      style={{
        paddingLeft: '2%',
        paddingRight: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: '1.2rem',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      }}>

      {/* Permiso y Recurso en una fila */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
        {/* Permiso */}
        <div>
          <label
            className="roles-title"
            style={{ fontSize: '.95rem', display: 'flex', alignItems: 'center', gap: 8 }}
          >
            Permiso:
            <button
              type="button"
              className="btn-action"
              onClick={() => setPermisosOpen(true)}
              title="Crear nuevo permiso"
              style={{ padding: '2px 8px', lineHeight: 1 }}
            >
              ＋ Permiso
            </button>
          </label>
          <br /><br />
          <select
            className={`infoField${errors.idPermiso ? ' infoField--error' : ''}`}
            value={form.idPermiso ?? 0}
            onChange={(e) => handleChange('idPermiso', e.target.value ? Number(e.target.value) : 0)}
            disabled={readOnly || mode === 'edit'}
          >
            <option value={0}>— Selecciona permiso —</option>
            {permisosOptions.map((p) => (
              <option key={p.idPermiso} value={p.idPermiso}>
                {p.nombre}
              </option>
            ))}
          </select>
          {errors.idPermiso && <small style={errorStyle}>{errors.idPermiso}</small>}
        </div>

        {/* Recurso */}
        <div>
          <label
            className="roles-title"
            style={{ fontSize: '.95rem', display: 'flex', alignItems: 'center', gap: 8 }}
          >
            Recurso:
            <button
              type="button"
              className="btn-action"
              onClick={() => setRecursosOpen(true)}
              title="Crear nuevo recurso"
              style={{ padding: '2px 8px', lineHeight: 1 }}
            >
              ＋ Recurso
            </button>
          </label>
          <br /><br />
          <select
            className={`infoField${errors.idRecurso ? ' infoField--error' : ''}`}
            value={form.idRecurso ?? ''}
            onChange={(e) => handleChange('idRecurso', e.target.value ? Number(e.target.value) : '')}
            disabled={readOnly || mode === 'edit'}
          >
            <option value="">— Selecciona recurso —</option>
            {filteredRecursos.map((r) => (
              <option key={r.idRecurso} value={r.idRecurso}>
                {r.displayName}
              </option>
            ))}
          </select>
          {errors.idRecurso && <small style={errorStyle}>{errors.idRecurso}</small>}
        </div>
      </div>

      {/* Drag and Drop para Roles */}
      <div>
        <label className="roles-title" style={{ fontSize: '.95rem', marginBottom: '0.5rem', display: 'block' }}>
          Roles:
        </label>
        <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>
          {readOnly 
            ? 'Modo de solo lectura. No se permiten cambios.'
            : 'Arrastra o usa los botones (+ / ×) para seleccionar múltiples roles'
          }
        </div>

        <div className="dnd-container">
          {/* Left: Available Roles */}
          {!readOnly && (
            <div
              className={`dnd-column ${dragOverColumn === 'available' ? 'drag-over' : ''}`}
              onDragOver={(e) => handleDragOver(e, 'available')}
              onDrop={(e) => handleDrop(e, 'available')}
            >
              <div className="dnd-header">Roles Disponibles</div>
              {availableRoles.length === 0 ? (
                <div className="dnd-empty">Todos los roles están seleccionados</div>
              ) : (
                availableRoles.map(role => (
                  <div
                    key={role.idRol}
                    className="dnd-item"
                    draggable
                    onDragStart={(e) => handleDragStart(e, role, 'available')}
                  >
                    <span>{role.nombre}</span>
                    <button
                      type="button"
                      onClick={() => addRoleLocal(role)}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '1.2em',
                        color: '#28a745',
                        cursor: 'pointer',
                        padding: '0 5px'
                      }}
                      title="Agregar rol"
                    >
                      ＋
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Right: Selected Roles */}
          <div
            className={`dnd-column ${dragOverColumn === 'selected' ? 'drag-over' : ''}`}
            style={readOnly ? { width: '100%', border: '1px solid #ddd', background: '#f9f9f9' } : {}}
            onDragOver={!readOnly ? (e) => handleDragOver(e, 'selected') : undefined}
            onDrop={!readOnly ? (e) => handleDrop(e, 'selected') : undefined}
          >
            <div className="dnd-header">{readOnly ? 'Roles Asignados' : 'Roles Seleccionados'}</div>
            {selectedRolesData.length === 0 ? (
              <div className="dnd-empty">Arrastra roles aquí para seleccionarlos</div>
            ) : (
              selectedRolesData.map(role => (
                <div
                  key={role.idRol}
                  className="dnd-item"
                  style={readOnly ? { filter: 'grayscale(1)', opacity: 0.8, cursor: 'default' } : {}}
                  draggable={!readOnly}
                  onDragStart={!readOnly ? (e) => handleDragStart(e, role, 'selected') : undefined}
                >
                  <span>{role.nombre}</span>
                  {readOnly ? null : (
                    (!isSuperAdmin && !role.idEmpresa) ? (
                      <span style={{ fontSize: '1.1em', opacity: 0.6, cursor: 'default', padding: '0 5px' }} title="Rol global (Solo Lectura)">🔒</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => removeRoleLocal(role)}
                        style={{
                          background: 'none',
                          border: 'none',
                          fontSize: '1.2em',
                          color: '#dc3545',
                          cursor: 'pointer',
                          padding: '0 5px'
                        }}
                        title="Remover rol"
                      >
                        ×
                      </button>
                    )
                  )}
                </div>
              ))
            )}
          </div>
        </div>
        {errors.selectedRoles && <small style={errorStyle}>{errors.selectedRoles}</small>}
      </div>
    </div>
  );

  const Footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
        flex: '0 0 auto',
      }}
    >
      <button className="btn-cancel" onClick={onClose}>{readOnly ? 'Cerrar' : 'Cancelar'}</button>
      {!readOnly && (
        <button
          className="btn-action"
          onClick={() => {
            if (validateAll()) {
              const finalSelectedRoles = form.selectedRoles.filter(roleId => !preAssignedRoles.includes(roleId));
              const finalForm = { ...form, selectedRoles: finalSelectedRoles };

              if (mode === 'new') {
                onSubmit(finalForm, mode);
              } else {
                onSubmit(form, mode);
              }
            }
          }}
          disabled={isSaveDisabled}
          style={{
            opacity: isSaveDisabled ? 0.5 : 1,
            cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
          }}
        >
          {mode === 'new' ? 'Guardar' : 'Actualizar'}
        </button>
      )}
    </div>
  );

  if (inline) {
    return (
      <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {Header}
        {Body}
        {Footer}

        {/* Modales embebidos */}
        <PermisosModal
          open={permisosOpen}
          mode="new"
          onClose={() => setPermisosOpen(false)}
          onSubmit={handleSubmitNuevoPermiso}
        />

        <RecursosModal
          open={recursosOpen}
          mode="new"
          onClose={() => setRecursosOpen(false)}
          onSubmit={handleSubmitNuevoRecurso}
        />

        {(savingPermiso || savingRecurso) && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.1)' }} />
        )}
      </div>
    );
  }

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: 'min(900px, 96vw)',
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {Header}
        {Body}
        {Footer}
      </div>

      {/* Modales de catálogos */}
      <PermisosModal
        open={permisosOpen}
        mode="new"
        onClose={() => setPermisosOpen(false)}
        onSubmit={handleSubmitNuevoPermiso}
      />

      <RecursosModal
        open={recursosOpen}
        mode="new"
        onClose={() => setRecursosOpen(false)}
        onSubmit={handleSubmitNuevoRecurso}
      />

      {(savingPermiso || savingRecurso) && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.1)' }} />
      )}
    </div>
  );
}
